import { AxiosInstance, InternalAxiosRequestConfig, AxiosStatic, AxiosResponse } from 'axios';
import type {
  RequestConfig,
  RequestInterceptors,
  Response,
  RequestOptions,
  RequestConstructorConfig,
} from './type';
import CacheManager from './CacheManager';
import { costValidate, gotoCostCenter, addExpiredTip } from './cost';

// token 失效 code 和 message
const invalidTokenCodes = ['9990001', '9990002', '9990003', '9990008'];
const invalidTokenMessages = ['INVALID_TOKEN', 'TOKEN_EXPIRED', 'TOKEN_NOT_EXIST'];

/**
 * axios拦截器封装，执行顺序 接口请求 -> 实例请求 -> 全局请求 -> 全局响应 -> 实例响应 -> 接口响应
 */
class WdzRequest {
  // axios 实例
  instance: AxiosInstance;
  // 实例自定义拦截器
  customInterceptors?: RequestInterceptors;
  // token 失效回调
  tokenInvalidCallback?: () => void;
  // 缓存管理
  private _cacheManager: CacheManager;
  // axios 静态对象
  private _axios: AxiosStatic;

  constructor(requestConfig: RequestConstructorConfig) {
    this._axios = requestConfig.axios || window.axios;
    if (!this._axios) {
      throw new Error('axios is not found');
    }

    this.instance = this._axios.create(requestConfig);
    this.customInterceptors = requestConfig.interceptors;
    this.tokenInvalidCallback = requestConfig.tokenInvalidCallback;
    this._cacheManager = new CacheManager();

    // 注册全局请求拦截
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // 从请求参数中提取 _ignoreCostTip 到 config
        if (config.params && typeof config.params === 'object') {
          const params = config.params as Record<string, unknown>;
          if ('_ignoreCostTip' in params) {
            config._ignoreCostTip = Boolean(params._ignoreCostTip);
            delete params._ignoreCostTip;
          }
        }
        if (
          config.data &&
          typeof config.data === 'object' &&
          !Array.isArray(config.data) &&
          !(config.data instanceof FormData)
        ) {
          const data = config.data as Record<string, unknown>;
          if ('_ignoreCostTip' in data) {
            config._ignoreCostTip = Boolean(data._ignoreCostTip);
            delete data._ignoreCostTip;
          }
        }
        // 如果没指定请求数据类型，默认 json
        if (!config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json;charset=UTF-8';
        }
        return config;
      },
      (err: any) => err,
    );

    // 注册全局响应拦截
    this.instance.interceptors.response.use(
      (res: AxiosResponse<Response>) => {
        const result = res.data;
        if (result) {
          if (result.isError) {
            // isError 为 true, 接口请求出错
            if (
              invalidTokenCodes.includes(result.code!) ||
              invalidTokenMessages.includes(result.message!)
            ) {
              // token 失效
              typeof this.tokenInvalidCallback === 'function' && this.tokenInvalidCallback();
            }
          } else {
            if (res.config.url === '/ovopark-organize/enterprise/getMyInfo') {
              // 缓存用户信息
              sessionStorage.setItem('userInfo', JSON.stringify(result.data));
            }
            costValidate(result, res.config.url!, this._axios, res.config._ignoreCostTip);
            // 正常返回数据
            return result.data;
          }
        }
        return Promise.reject(result || { message: '请求出错，请稍候再试' });
      },
      (err: any) => {
        const { response } = err;
        if (response) {
          return Promise.reject(response);
        }
        if (!err.message) {
          err.message = '请求出错，请稍候再试';
        }
        throw err;
      },
    );

    // 使用实例拦截器
    this.instance.interceptors.request.use(
      this.customInterceptors?.request,
      this.customInterceptors?.requestCatch,
    );
    this.instance.interceptors.response.use(
      this.customInterceptors?.response,
      this.customInterceptors?.responseCatch,
    );
  }

  /** 静态方法，跳转费用中心 */
  static gotoCostCenter = gotoCostCenter;
  /**
   * 静态方法，添加过期提示
   * 本地开发测试用！！！
   * 本地开发测试用！！！
   * 本地开发测试用！！！
   */
  // static addExpiredTip = addExpiredTip;

  request<T>(config: RequestConfig<any, T>): Promise<T> {
    return new Promise((resolve, reject) => {
      // 判断是否开启缓存，如果 config.updateCache 为 true，则不使用缓存，直接请求接口
      if (config.cache && !config.updateCache) {
        const cacheExecutor = this._cacheManager.get<T>(config);
        if (cacheExecutor) {
          // 已发起过请求
          cacheExecutor.then(resolve).catch(reject);
          return;
        }
      }
      // 单个请求的拦截器
      if (!config.headers) {
        config.headers = new this._axios.AxiosHeaders();
      }
      if (config.interceptors?.request) {
        config = config.interceptors.request(config as InternalAxiosRequestConfig);
      }

      this.instance
        .request<any, T>(config)
        .then((res) => {
          // 单个响应的拦截器
          if (config.interceptors?.response) {
            res = config.interceptors.response(res);
          }
          config.cache && this._cacheManager.set(config, res);
          resolve(res);
        })
        .catch((err: any) => {
          // 单个响应异常的拦截器
          if (config.interceptors?.responseCatch) {
            err = config.interceptors.responseCatch(err);
          }
          config.cache && this._cacheManager.set(config, null, err);
          reject(err);
        });
    });
  }

  /** get请求 */
  get<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      url,
      method: 'GET',
      params: data,
      headers: {
        'Cache-Control': 'no-cache',
      },
      ...options,
    });
  }
  /** post请求，body为form表单 */
  postForm<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      url,
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data,
      ...options,
    });
  }
  /** post请求，body为json */
  post<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>({
      url,
      method: 'POST',
      data,
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
      ...options,
    });
  }
}

export default WdzRequest;

export { gotoCostCenter };
